/**
 * 医療脱毛ページの料金ナビ（LADIES / MEN）。
 *
 * 性別ごとに料金へ1クリックで到達できるよう、男女を明確に分離する。
 * 金額は持たず、料金セクション内のアンカーへの導線のみを担う。
 *
 * Version 101：
 *  ・「女性目次 → 女性料金／男性目次 → 男性料金」の順に並べるため、
 *    男女それぞれを単体で描画できるようにした。
 *  ・男性側は「男性目次〜男性料金」をひと続きのダークエリアへ統一するため、
 *    tone="dark"（チャコール背景 × 白文字 × 薄い白罫線のボタン）を用意。
 *  ・性別カラーの強い出し分けは避け、SBDCのブランドトーン
 *    （グレージュ／チャコール／シャンパンゴールド）で統一する。
 */
export type PriceNavLink = { label: string; anchor: string };

export type PriceNavGroup = {
  en: string;
  jp: string;
  links: PriceNavLink[];
};

/** 女性料金の導線（女性グループ見出し／女性の部位別行） */
export const ladiesPriceNav: PriceNavGroup = {
  en: "LADIES",
  jp: "女性の医療脱毛",
  links: [
    { label: "女性料金", anchor: "hair-ladies" },
    { label: "全身", anchor: "hair-ladies-full" },
    { label: "顔", anchor: "hair-ladies-face" },
    { label: "VIO", anchor: "hair-ladies-vio" },
  ],
};

/** 男性料金の導線（男性グループ見出し／ヒゲ・VIO・ボディ） */
export const mensPriceNav: PriceNavGroup = {
  en: "MEN",
  jp: "男性の医療脱毛",
  links: [
    { label: "男性料金", anchor: "hair-mens" },
    { label: "ヒゲ", anchor: "hair-mens-beard" },
    { label: "VIO", anchor: "hair-mens-vio" },
    { label: "ボディ", anchor: "hair-mens-body" },
  ],
};

export default function HairRemovalPriceNav({
  group,
  tone = "light",
}: {
  group: PriceNavGroup;
  tone?: "light" | "dark";
}) {
  const isDark = tone === "dark";

  return (
    <div>
      <div className="flex items-baseline gap-4">
        <span
          className={`en-eyebrow text-[10px] md:text-[11px] ${
            isDark ? "text-accent-400" : "text-secondary-700"
          }`}
        >
          {group.en}
        </span>
        <span className={`h-px flex-1 ${isDark ? "bg-background-50/20" : "bg-foreground-950/10"}`} />
      </div>

      <h3
        className={`mt-4 font-heading text-[19px] leading-[1.6] md:text-[21px] ${
          isDark ? "text-background-50" : "text-foreground-950"
        }`}
      >
        {group.jp}
      </h3>

      <span className="mt-5 block h-px w-9 bg-accent-500" />

      <div
        className={`mt-6 grid grid-cols-2 gap-px overflow-hidden border ${
          isDark ? "border-background-50/15 bg-background-50/15" : "border-foreground-950/12 bg-foreground-950/12"
        }`}
      >
        {group.links.map((link) => (
          <a
            key={link.anchor}
            href={`#${link.anchor}`}
            className={`group/link flex items-center justify-between gap-2 px-4 py-5 text-[13px] transition-colors md:text-[13.5px] ${
              isDark
                ? "bg-foreground-950 text-background-50 hover:bg-foreground-900"
                : "bg-background-50 text-foreground-900 hover:bg-secondary-100/70"
            }`}
          >
            <span className="whitespace-nowrap">{link.label}</span>
            <i
              className={`ri-arrow-down-line text-[15px] transition-all group-hover/link:translate-y-0.5 ${
                isDark
                  ? "text-background-50/50 group-hover/link:text-accent-400"
                  : "text-foreground-400 group-hover/link:text-accent-700"
              }`}
              aria-hidden
            />
          </a>
        ))}
      </div>
    </div>
  );
}